import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStoreState } from 'easy-peasy';

import { OverlayTrigger, Tooltip, Image } from "react-bootstrap";

import DynamicTableInfinite from "../../../widgets/dynamicTableInfinite/DynamicTableInfinite.jsx";
import HelperMetiers360 from "../../../../services/HelpersMetiers360.js";

import TooltipListComponent from "../../../widgets/tooltipList/TooltipListComponent.jsx";
import useInfiniteLoadingLogic from "../../../../hooks/useInfiniteLogic.jsx";
import useCustomGetStoreState from "../../../../hooks/useCustomGetStoreState.jsx";
import './StoryTableComponent.scss';


const StoryTableComponent = ({ stories }) => {

    const navigate = useNavigate();
    const location = useLocation();

    const [sortedStories, setSortedStories] = useState([]);
    const [storiesToDisplay, setStoriesToDisplay] = useState([]);
    const [shouldSort, setShouldSort] = useState(false);

    useEffect(() => {
        setShouldSort(true);
        setSortedStories([...stories]);

    }, [stories]);

    const {hasMore, fetchNext: fetchNextStories} = useInfiniteLoadingLogic(sortedStories, storiesToDisplay, setStoriesToDisplay, 100);
    
    const storySlugs = useStoreState(actions => actions.actionSlugs.actionSlugsDispatcher('stories'));
    const { allSequences, isFetchingSequences } = useCustomGetStoreState("sequences");

    const producersName = (producers) => producers.map((producer) => producer.clientName ).map(String)?.join(', ');

    const codeRome = (romes) => romes?.join(', ');


    const labelVR='VR360';

    const getListSequences = (story) => {
        return allSequences.filter(sequence => {
            return sequence.stories.includes(story.uniqueId)
        })
    }

    if (stories.length === 0 )
        {return <></>;}

    const sortType = [
        {
            value: 'thumbnail', label: 'Vignette',
            test: stories[0].links.thumbnail !== undefined,
            display: (story) => (
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip>
                            <Image src={story.links.thumbnail[128]}/>
                        </Tooltip>
                    }    
                >
                    <Image src={story.links.thumbnail[512]} className="img-overlay" thumbnail />
                </OverlayTrigger>
            ),
            flatDisplay: (story) => story.links.thumbnail[512]
        },
        {
            value: 'title', label: "Nom",
            test : stories[0].name !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
            display: (story) => story.name
        },
        {
            value: 'sequences', label: 'Parcours',
            test: stories[0].isUsedInSequence !== 'undefined',
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getListSequences(a)?.length, getListSequences(b)?.length),
            display: (story) => {
                if (!story.isUsedInSequence) {
                    return 0
                }
                
                const listOfSequences = getListSequences(story);

                const generateIdCallback = (uniqueId) => {
                    return `sequence-${uniqueId}${story.uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={listOfSequences}
                    keyToDisplay='name'
                    generateIdCallback={generateIdCallback}
                    isFetching={isFetchingSequences}
                 />
            },
            flatDisplay: (story) => getListSequences(story).length
        },
        {
            value: 'position', label: "Position",
            test : stories[0].position !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.position, b.position),
            display: (story) => story.position
        },
        {
            value: 'type', label: "Media",
            test : stories[0].type !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.is360, b.is360),
            display: (story) => story.is360 ? labelVR:story.type
        },
        {
            value: 'fromVideo', label: "Extrait de",
            test : stories[0].fromVideo !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.fromVideo, b.fromVideo),
            display: (story) => story.fromVideo ? story.fromVideo:'-'
        },
        {
            value: 'date', label: "Publication",
            test : stories[0].releaseDate !== undefined,
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.releaseDate, b.releaseDate),
            display: (story) => HelperMetiers360.getdisplayDateType(story.releaseDate, 'day')
        },
        {
            value: 'lastUpdated', label: "Mise à jour",
            test : stories[0].lastUpdated !== undefined,
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.lastUpdated, b.lastUpdated),
            display: (story) => HelperMetiers360.getdisplayDateType(story.lastUpdated, 'day')
        },
        {
            value: 'brokenLink', label: "Lien cassé",
            test : HelperMetiers360.isArrayContainsValue(stories, "isExternalLinkBroken"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.isExternalLinkBroken, b.isExternalLinkBroken),
            display: (story) => story.isExternalLinkBroken ? 
                <i className='fas fa-times' /> :'',
            flatDisplay: (story) => story.isExternalLinkBroken ? "Oui" : "-",
        },
        {
            value: 'producers', label: "Producteur",
            test : stories[0].producers !== undefined,
            method: (a, b) => HelperMetiers360.sortStringArray(a.producers?.map(x => x.clientName), b.producers?.map(x => x.clientName)),
            display: (story) => story.producers.length ? producersName(story.producers) : '-'
        },
        {
            value: 'view', label: "Vues",
            test : stories[0].viewCount !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.viewCount, b.viewCount),
            display: (story) => story.viewCount ? story.viewCount : 0
        },
        {
            value: 'like', label: <i className="far fa-thumbs-up" />,
            test : stories[0].isLiked !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.isLiked, b.isLiked),
            display: (story) => story.isLiked ? story.isLiked : 0
        },
        {
            value: 'dislike', label: <i className="far fa-thumbs-down" />,
            test : stories[0].isUnliked !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.isUnliked, b.isUnliked),
            display: (story) => story.isUnliked ? story.isUnliked : 0
        },
        {
            value: 'romes', label: "Codes Rome",
            test : stories[0].romes !== undefined,
            method: (a, b) => HelperMetiers360.sortStringArray(a.romes, b.romes),
            display: (story) => story.romes.length ? codeRome(story.romes) : '-'
        },
        {
            value: 'greenfact', label: "Greenfact",
            test : stories[0].greenfact !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.greenfact, b.greenfact),
            display: (story) => story.greenfact ? 'X' : '-'
        },
        {
            value: 'featured', label: "Mise en avant",
            test : stories[0].featured !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.featured, b.featured),
            display: (story) => story.featured ? 'X' : '-'
        },
        {
            value: 'sequenceOnly', label: "Uniquement parcours",
            test: stories[0].sequenceOnly !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.sequenceOnly, b.sequenceOnly),
            display: (story) => story.sequenceOnly ? 'X' : '-'
        },
        {
            value: 'private', label: "Privée",
            test : stories[0].private !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.private, b.private),
            display: (story) => story.private ? 'X' : '-'
        },
        {
            value: 'freemium', label: "Free",
            test : stories[0].freemium !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.freemium, b.freemium),
            display: (story) => story.freemium ? 'X' : '-'
        },
        {
            value: 'typeOfStory', label: "Type",
            test : stories[0].typeOfStory !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.typeOfStory?.label, b.typeOfStory?.label),
            display: (story) => story.typeOfStory ? story.typeOfStory.label:'-'
        },
    ];

    const onSelectStory = (uniqueID) => {
         // eslint-disable-next-line no-unused-expressions
         storySlugs.readOne
             ? navigate(storySlugs.readOne.replace(':uniqueId', uniqueID), { state: { olderSearchParams: location.search } })
            : null;
    }

    return storiesToDisplay.length > 0 
        && <DynamicTableInfinite
            contentTable = {storiesToDisplay}
            contentSort = {sortType}
        valueInitSort="date"
            index = 'uniqueId'
            handleClick = {onSelectStory}
            fetchData={fetchNextStories}
            hasMore={hasMore}
            setSortedContent={setSortedStories}
            sortedContent={sortedStories}
            sortState={[shouldSort, setShouldSort]}
        withParams={true}
            tableName="storiesTable"
            filename="stories" />;
        
}

export default StoryTableComponent